import React, { Fragment, useContext } from 'react';
import { ButtonStyled, TextStyled } from '@nike/nike-design-system-components';
import PropTypes from 'prop-types';
import { Favorite, Orders, QuestionCircle, Store } from '@nike/nike-design-system-icons';
import {
  ICON_CLASS,
  ICON_LINK_CLASS,
  JOIN_BTN_CLASS,
  SIGN_IN_BTN_CLASS,
  SIGN_IN_BUTTONS_CLASS,
  SIGN_IN_WRAPPER_CLASS,
  USER_ITEM_CLASS,
} from '../MobileMenu/styles';
import Link from '../Link';
import AnalyticsEventTypes from '../../utils/analyticsEvent';
import useBodega from '../../utils/hooks/bodegaHook';
import {
  BODEGA_DEFAULTS,
  GLOBAL_NAV_MOBILE_ORDERS,
  GLOBAL_NAV_FIND_A_STORE,
} from '../../constants';
import templateString from '../../utils/templateString';
import useLocale from '../../utils/hooks/localeHook';
import { getLocalizedUrl, MembershipUrl } from '../../utils/url';
import useFeatureFlags from '../../utils/hooks/featureFlagsHook';
import { AnalyticsContext } from '../../context/analyticsContext';
import { BagWithCount } from '../UserTools/bagWithCount';
import { useCartAndFavorites } from '../../utils/hooks/useCartAndFavorites';

const ordersUrl = getLocalizedUrl('orders');
const retailUrl = getLocalizedUrl('retail');
const helpUrl = getLocalizedUrl('help');
const registerUrl = getLocalizedUrl('register');

/**
 * abstracted from MobileMenu to clean up the code
 * @param {object} props
 * @param {bool} props.isLoggedIn needed so we know if we should display sign in/join us
 * or account related buttons
 * @returns {React.ReactElement}
 */
const MobileUtilityMenu = ({ isLoggedIn }) => {
  const locale = useLocale();
  const membershipUrl = MembershipUrl(locale);
  const { analyticsClient } = useContext(AnalyticsContext);
  const {
    FAVORITES,
    BAG,
    ORDERS,
    FIND_A_STORE,
    HELP,
    LEARN_MORE,
    JOIN_US_TITLE,
    JOIN_US,
    SIGN_IN,
  } = BODEGA_DEFAULTS;

  const { isCartEnabled, isFavoritesEnabled, itemsInCart, cartUrl, favsUrl } =
    useCartAndFavorites();
  const JOIN_US_TRANSLATION = useBodega('nav_title_joinus_membership', JOIN_US_TITLE);
  const SIGN_IN_TRANSLATION = useBodega('nav_title_signin_membership', SIGN_IN);

  const isOrdersEnabled = useFeatureFlags(GLOBAL_NAV_MOBILE_ORDERS);
  const isFindStoreEnabled = useFeatureFlags(GLOBAL_NAV_FIND_A_STORE);

  const mobileUserItems = [
    {
      analyticsEventName: AnalyticsEventTypes.CLICK_HELP_MENU,
      icon: QuestionCircle,
      iconClass: 'icon-help-menu',
      path: 'menu:help',
      msg: useBodega('nav_header_label_help', HELP),
      url: helpUrl(locale),
    },
  ];

  const orderLink = {
    analyticsEventName: AnalyticsEventTypes.CLICK_NAV_SHORTCUT,
    icon: Orders,
    iconClass: 'icon-account',
    path: 'menu:orders',
    msg: useBodega('nav_header_label_orders', ORDERS),
    url: ordersUrl(locale),
  };

  const favoritesLink = {
    analyticsEventName: AnalyticsEventTypes.CLICK_NAV_SHORTCUT,
    icon: Favorite,
    iconClass: 'icon-favorites',
    path: 'menu:favorites',
    msg: useBodega('nav_header_label_favorites', FAVORITES),
    url: favsUrl,
  };

  const cartLink = {
    analyticsEventName: AnalyticsEventTypes.CLICK_NAV_CART,
    icon: BagWithCount,
    iconClass: 'icon-cart',
    path: 'cart',
    msg: useBodega('nav_header_label_bag', BAG),
    url: cartUrl,
    ariaLabel: `${useBodega('nav-header-label-carticon', BAG)}: ${itemsInCart}`,
  };

  const findAStoreLink = {
    analyticsEventName: AnalyticsEventTypes.CLICK_NAV_STORE_LOCATOR,
    icon: Store,
    iconClass: 'icon-store-locator',
    path: 'store:locator',
    msg: useBodega('nav_header_find_a_store', FIND_A_STORE),
    url: retailUrl(locale),
  };

  // add favorites, cart, orders and find a store links in a specific order
  // to mobileUserItems array only if they are enabled in that country
  isFavoritesEnabled && isLoggedIn && mobileUserItems.splice(0, 0, favoritesLink);
  isCartEnabled && mobileUserItems.splice(1, 0, cartLink);
  isOrdersEnabled && mobileUserItems.splice(2, 0, orderLink);
  isFindStoreEnabled && mobileUserItems.splice(3, 0, findAStoreLink);

  const learnMoreLink = (
    <Link
      clickEventPayload={{
        props: { path: 'Learn More' },
        type: AnalyticsEventTypes.CLICK_NAV_MEMBERSHIP,
      }}
      dataTestId="learn-more"
      href={membershipUrl}
      key="learn-more"
    >
      {useBodega('nav_title_learnmore_membership', LEARN_MORE)}
    </Link>
  );

  const joinMessage = templateString(useBodega('nav_message_join_membership', JOIN_US), {
    learnMore: learnMoreLink,
  });

  return (
    <Fragment>
      {!isLoggedIn && (
        <div className={SIGN_IN_WRAPPER_CLASS}>
          <TextStyled appearance="title4" color="secondary">
            {joinMessage}
          </TextStyled>
          <div className={SIGN_IN_BUTTONS_CLASS}>
            <ButtonStyled
              Component={'a'}
              className={JOIN_BTN_CLASS}
              href={membershipUrl}
              onClick={() => {
                analyticsClient?.clickNavMembership({ path: 'join us' });
              }}
              size="small"
            >
              {JOIN_US_TRANSLATION}
            </ButtonStyled>
            <ButtonStyled
              className={SIGN_IN_BTN_CLASS}
              href={registerUrl(locale)}
              onClick={event => {
                event.preventDefault();
                analyticsClient?.clickNavMembership({ path: 'sign in' });
                window.webShellClient.identity.signIn().finally();
              }}
              size="small"
            >
              {SIGN_IN_TRANSLATION}
            </ButtonStyled>
          </div>
        </div>
      )}
      <div className={USER_ITEM_CLASS}>
        {mobileUserItems.map(mobileUserItem => (
          <Link
            ariaLabel={mobileUserItem.ariaLabel}
            className={`${ICON_LINK_CLASS} ${mobileUserItem.iconClass}`}
            clickEventPayload={{
              props: { path: mobileUserItem.path },
              type: mobileUserItem.analyticsEventName,
            }}
            href={mobileUserItem.url}
            key={mobileUserItem.path}
          >
            <mobileUserItem.icon className={ICON_CLASS} count={itemsInCart} size="m" />
            <p>{mobileUserItem.msg}</p>
          </Link>
        ))}
      </div>
    </Fragment>
  );
};

MobileUtilityMenu.propTypes = {
  isLoggedIn: PropTypes.bool,
};

export default MobileUtilityMenu;
